/**
 * Session côté interface. Les gardes et `peut()` sont un CONFORT D'UX : la décision réelle est prise par l'API
 * (handlers MSW en simulation, guards NestJS en production). Chaque écran protégé a son équivalent API testé.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router';
import type { S } from '@myron/contracts';
import type { Permission, Role } from '@myron/domain';
import { ApiErreur, appel, brancherSessionExpiree, definirJetonCsrf, post } from '../api/client';
import { SkeletonListe } from '@myron/design-system';

export type Session = S<'SessionCourante'> & { etape?: 'MFA_REQUISE' | 'ENROLEMENT_MFA_REQUIS' | 'CONNECTE' };

interface CtxSession {
  session: Session | null;
  chargement: boolean;
  /** Connecté et MFA éventuelle franchie. */
  connecte: boolean;
  estInterne: boolean;
  estClient: boolean;
  peut: (p: Permission) => boolean;
  aRole: (...r: Role[]) => boolean;
  definir: (s: Session | null) => void;
  rafraichir: () => Promise<unknown>;
  deconnecter: () => Promise<void>;
}
const Ctx = createContext<CtxSession | null>(null);
export const CLE_SESSION = ['session'] as const;

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: CLE_SESSION,
    queryFn: async () => {
      try {
        return await appel<Session>('GET', '/auth/session');
      } catch (e) {
        if (e instanceof ApiErreur && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 30_000,
    retry: false,
  });
  const session = q.data ?? null;
  useEffect(() => {
    definirJetonCsrf(session?.jetonCsrf ?? null);
  }, [session?.jetonCsrf]);

  const definir = useCallback((s: Session | null) => {
    definirJetonCsrf(s?.jetonCsrf ?? null);
    qc.setQueryData(CLE_SESSION, s);
  }, [qc]);

  useEffect(() => {
    brancherSessionExpiree(() => {
      qc.setQueryData(CLE_SESSION, null);
      const retour = encodeURIComponent(window.location.pathname + window.location.search);
      if (!window.location.pathname.startsWith('/connexion')) window.history.pushState({}, '', `/connexion?expiree=1&retour=${retour}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    });
    return () => brancherSessionExpiree(null);
  }, [qc]);

  const valeur = useMemo<CtxSession>(() => {
    const connecte = !!session && (session.etape ?? 'CONNECTE') === 'CONNECTE';
    const perms = new Set<string>(connecte ? session?.permissions ?? [] : []);
    return {
      session,
      chargement: q.isPending,
      connecte,
      estInterne: session?.utilisateur.type === 'INTERNE',
      estClient: session?.utilisateur.type === 'CLIENT',
      peut: (p) => perms.has(p),
      aRole: (...r) => !!session?.roles?.some((x) => r.includes(x as Role)),
      definir,
      rafraichir: () => qc.invalidateQueries({ queryKey: CLE_SESSION }),
      deconnecter: async () => {
        await post('/auth/deconnexion').catch(() => undefined);
        definirJetonCsrf(null);
        qc.clear();
        qc.setQueryData(CLE_SESSION, null);
      },
    };
  }, [session, q.isPending, definir, qc]);

  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
}

export function useSession() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useSession hors SessionProvider');
  return c;
}

/** Garde d'espace : redirige vers la connexion (ou l'étape MFA) si nécessaire ; 403 si espace non autorisé. */
export function RequireEspace({ espace, children }: { espace: 'pro' | 'admin'; children: ReactNode }) {
  const { session, chargement, connecte, peut } = useSession();
  const loc = useLocation();
  if (chargement) return <div className="my-conteneur my-section"><SkeletonListe /></div>;
  const retour = encodeURIComponent(loc.pathname + loc.search);
  if (!session) return <Navigate to={`/connexion?retour=${retour}`} replace />;
  if (session.etape === 'MFA_REQUISE') return <Navigate to={`/connexion/mfa?retour=${retour}`} replace />;
  if (session.etape === 'ENROLEMENT_MFA_REQUIS') return <Navigate to={`/connexion/enrolement-mfa?retour=${retour}`} replace />;
  if (!connecte) return <Navigate to="/connexion" replace />;
  const permission: Permission = espace === 'pro' ? 'portail.acces' : 'admin.acces';
  if (!peut(permission)) return <Navigate to="/erreur/403" replace />;
  return <>{children}</>;
}

/** Affiche le contenu seulement si la permission est détenue (masquage d'UX ; l'API refuse de son côté). */
export function SiPermission({ permission, children, sinon = null }: { permission: Permission | Permission[]; children: ReactNode; sinon?: ReactNode }) {
  const { peut } = useSession();
  const liste = Array.isArray(permission) ? permission : [permission];
  return <>{liste.some(peut) ? children : sinon}</>;
}

/** Garde d'écran : 403 si la permission manque. */
export function RequirePermission({ permission, children }: { permission: Permission | Permission[]; children: ReactNode }) {
  const { peut } = useSession();
  const liste = Array.isArray(permission) ? permission : [permission];
  if (!liste.some(peut)) return <Navigate to="/erreur/403" replace />;
  return <>{children}</>;
}
